import React from 'react'
import { Row, Col, Button } from 'reactstrap'
import { DataGrid } from '@mui/x-data-grid'
import { Chip } from '@mui/material'
import { withFirebase } from '../../components/Fb'
import EditButton from '../../components/Buttons/EditButton'
import DeleteButton from '../../components/Buttons/DeleteButton'
import AffiliatePartnerEditor, { PARTNER_STATES } from '../../components/Details/AffiliatePartnerEditor'
import AffiliateDeleteDialog from '../../components/Dialogs/AffiliateDeleteDialog'

const stateLabel = (value) => {
  const found = PARTNER_STATES.find((s) => s.value === value)
  return found ? found.label : value || ''
}

/**
 * Affiliate partners - the billable parties behind the campaigns. Kept separate from
 * campaigns so one partner can run several campaigns over time without its contact and
 * invoicing details being copied into each of them.
 */
class AffiliatePartnersContainer extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      loading: true,
      partners: [],
      stateFilter: 'active',
      editorOpen: false,
      selectedPartner: null,
      deleteTarget: null,
      deleting: false,
    }
  }

  componentDidMount() {
    this.updateData()
  }

  updateData = async () => {
    try {
      const partners = await this.props.firebase.getAffiliates()
      this.setState({ partners, loading: false })
    } catch (error) {
      console.error('Affiliate: partners failed to load', error)
      this.setState({ partners: [], loading: false })
    }
  }

  getFiltered = () => {
    const { partners, stateFilter } = this.state
    if (stateFilter === 'all') return partners
    return partners.filter((p) => (p.state || 'active') === stateFilter)
  }

  addNewPartner = () => {
    this.setState({ editorOpen: true, selectedPartner: null })
  }

  editPartner = (partner) => {
    this.setState({ editorOpen: true, selectedPartner: partner })
  }

  closeEditor = () => {
    this.setState({ editorOpen: false, selectedPartner: null })
  }

  handleSave = async (partner) => {
    if (partner.id) {
      await this.props.firebase.updateAffiliate(partner)
    } else {
      await this.props.firebase.createAffiliate(partner)
    }
    this.setState({ editorOpen: false, selectedPartner: null, loading: true }, this.updateData)
  }

  confirmDelete = async () => {
    const target = this.state.deleteTarget
    if (!target) return
    this.setState({ deleting: true })
    await this.props.firebase.deleteAffiliate(target.id)
    this.setState({ deleting: false, deleteTarget: null, loading: true }, this.updateData)
  }

  getColDefinitions = () => [
    { field: 'name', headerName: 'Nimi', flex: 2, minWidth: 160 },
    { field: 'organisationname', headerName: 'Organisaatio', flex: 2, minWidth: 140 },
    { field: 'contactname', headerName: 'Yhteyshenkilö', flex: 1.5, minWidth: 130 },
    { field: 'email', headerName: 'Sähköposti', flex: 2, minWidth: 160 },
    { field: 'businessid', headerName: 'Y-tunnus', flex: 1, minWidth: 100 },
    {
      field: 'state',
      headerName: 'Tila',
      flex: 1,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          size='small'
          label={stateLabel(params.row.state || 'active')}
          color={(params.row.state || 'active') === 'active' ? 'primary' : 'default'}
          variant={(params.row.state || 'active') === 'active' ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'edit',
      headerName: ' ',
      sortable: false,
      filterable: false,
      width: 120,
      renderCell: (params) => <EditButton disablediv onClick={() => this.editPartner(params.row)} />,
    },
    {
      field: 'delete',
      headerName: ' ',
      sortable: false,
      filterable: false,
      width: 110,
      renderCell: (params) => (
        <DeleteButton disablediv onClick={() => this.setState({ deleteTarget: params.row })} />
      ),
    },
  ]

  renderStateFilters = () => {
    const options = [...PARTNER_STATES, { label: 'Kaikki', value: 'all' }]
    return options.map((option) => (
      <Chip
        key={option.value}
        label={option.label}
        clickable
        size='small'
        color='primary'
        variant={this.state.stateFilter === option.value ? 'filled' : 'outlined'}
        onClick={() => this.setState({ stateFilter: option.value })}
        style={{ marginRight: 4 }}
      />
    ))
  }

  render() {
    if (this.state.loading) {
      return <div className='content'>Loading ...</div>
    }

    if (this.state.editorOpen) {
      return (
        <div className='content'>
          <AffiliatePartnerEditor
            partner={this.state.selectedPartner}
            onSave={this.handleSave}
            onCancel={this.closeEditor}
          />
        </div>
      )
    }

    return (
      <div className='content'>
        <Row className='mb-2'>
          <Col>
            <Button size='sm' onClick={this.addNewPartner}>
              Lisää kumppani
            </Button>
          </Col>
        </Row>
        <Row>
          <Col className='mb-2'>{this.renderStateFilters()}</Col>
        </Row>
        <Row>
          <Col xs='12'>
            <div style={{ display: 'flex', minHeight: 'calc(100vh - 260px)', backgroundColor: '#FFFFFF' }}>
              <div style={{ flexGrow: 1, minWidth: 0 }}>
                <DataGrid
                  rows={this.getFiltered()}
                  columns={this.getColDefinitions()}
                  density='compact'
                  initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                  pageSizeOptions={[25, 50, 100]}
                />
              </div>
            </div>
          </Col>
        </Row>
        <AffiliateDeleteDialog
          open={!!this.state.deleteTarget}
          type='partner'
          name={this.state.deleteTarget ? this.state.deleteTarget.name : ''}
          working={this.state.deleting}
          onCancel={() => this.setState({ deleteTarget: null })}
          onConfirm={this.confirmDelete}
        />
      </div>
    )
  }
}

export default withFirebase(AffiliatePartnersContainer)
